import type {i18n as I18nInstance} from 'i18next';
import bg from '../locales/bg/translation.json';
import ca from '../locales/ca/translation.json';
import cs from '../locales/cs/translation.json';
import de from '../locales/de/translation.json';
import el from '../locales/el/translation.json';
import en from '../locales/en/translation.json';
import es from '../locales/es/translation.json';
import et from '../locales/et/translation.json';
import fr from '../locales/fr/translation.json';
import hu from '../locales/hu/translation.json';
import it from '../locales/it/translation.json';
import ko from '../locales/ko/translation.json';
import nl from '../locales/nl/translation.json';
import pl from '../locales/pl/translation.json';
import pt from '../locales/pt/translation.json';
import ro from '../locales/ro/translation.json';
import ru from '../locales/ru/translation.json';
import uk from '../locales/uk/translation.json';

export const uiKitTranslations = {
  en,
  es,
  it,
  de,
  fr,
  nl,
  ko,
  hu,
  ru,
  cs,
  bg,
  pt,
  ro,
  et,
  pl,
  ca,
  el,
  uk,
} as const;

export const uiKitI18nResources = {
  en: {translation: en},
  es: {translation: es},
  it: {translation: it},
  de: {translation: de},
  fr: {translation: fr},
  nl: {translation: nl},
  ko: {translation: ko},
  hu: {translation: hu},
  ru: {translation: ru},
  cs: {translation: cs},
  bg: {translation: bg},
  pt: {translation: pt},
  ro: {translation: ro},
  et: {translation: et},
  pl: {translation: pl},
  ca: {translation: ca},
  el: {translation: el},
  uk: {translation: uk},
} as const;

export type UiKitLocale = keyof typeof uiKitTranslations;

export const UI_KIT_I18N_NAMESPACE = 'translation';

export const UI_KIT_LANGUAGE_FALLBACKS: Partial<Record<UiKitLocale, UiKitLocale>> = {
  ca: 'es',
};

export interface RegisterUiKitI18nOptions {
  /**
   * i18next namespace to register the ui-kit keys under. Defaults to `translation`,
   * which matches what `useTranslation()` resolves to inside the ui-kit components.
   */
  namespace?: string;
  /**
   * When true, ui-kit keys overwrite existing consumer keys with the same name.
   * Defaults to `false` so consumer translations always win on a collision.
   */
  overwrite?: boolean;
}

/**
 * Register ui-kit translations on the consumer's i18next instance. Call once,
 * after `i18n.init(...)`, so components like Button/Pagination/Section can
 * resolve their built-in keys (`please_wait`, `loading`, `error`, ...).
 *
 * Uses deep-merge so consumer keys in the same namespace are preserved.
 */
export function registerUiKitI18n(
  i18n: I18nInstance,
  options: RegisterUiKitI18nOptions = {},
): void {
  const namespace = options.namespace ?? UI_KIT_I18N_NAMESPACE;
  const overwrite = options.overwrite ?? false;

  for (const [lng, bundle] of Object.entries(uiKitTranslations)) {
    const fallback = UI_KIT_LANGUAGE_FALLBACKS[lng as UiKitLocale];
    const effectiveBundle =
      fallback && Object.keys(bundle).length === 0 ? uiKitTranslations[fallback] : bundle;

    i18n.addResourceBundle(lng, namespace, effectiveBundle, true, overwrite);
  }
}
